import { NativeSelect } from '../components/app-ui/native-select';
import { Button } from '../components/app-ui/button';
import {
  ArrowRight,
  BellRing,
  Camera,
  Check,
  ChevronRight,
  CircleDollarSign,
  Globe2,
  Map,
  Menu,
  Moon,
  PackageCheck,
  Play,
  Route,
  ShieldCheck,
  Sparkles,
  Sun,
  Users,
  Vote,
  WalletCards,
  X,
} from "../components/app-ui/icons/index";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Brand from '../components/Brand';
import PlannerExperience from '../components/PlannerExperience';
import { useTheme } from '../contexts/ThemeContext';
import { landingDetails } from '../landingDetails';

type MarketingLanguage = 'en' | 'tr' | 'sv';

const pageCopy = {
  en: {
    nav: ['Product', 'EV & maps', 'Together', 'After the trip', 'Plans'],
    login: 'Open web app',
    eyebrow: 'ONE LIVING PLAN · BEFORE, DURING & AFTER',
    title: 'Tragenda is your',
    titleAccent: 'travel agenda.',
    subtitle: 'Plan the road trip in your head. See what every stop changes. Get everyone ready. Keep the story when you get home.',
    primary: 'Start planning', secondary: 'Explore the product',
    proof: ['Private by default', 'Built for solo & group trips', 'EV and fuel aware'],
    productEyebrow: 'THE WHOLE TRIP, IN ONE PLACE',
    productTitle: 'Less trip admin. More road ahead.',
    productBody: 'A living itinerary connects the route, the people and everything the trip creates — without turning travel into project management.',
    features: [
      ['Living itinerary', 'Build days and stops around a real route. Move the plan and the rest of the trip keeps up.'],
      ['Map intelligence', 'See drive time, distance, detour impact and what is actually along the way before you add it.'],
      ['Talk & decide', 'Keep chat beside the plan, turn an idea into a vote and leave the final decision where everyone can find it.'],
      ['Money that explains itself', 'Track currencies, receipts, custom groups, guests, balances and who owes whom without a separate sheet.'],
      ['Pack & ready', 'Create lists for each bag or the car. Add your own categories and see who brings each item.'],
      ['Story & share', 'Save private moments, then publish only the route stops you choose as a reusable guide after the trip.'],
    ],
    evEyebrow: 'BUILT FOR EV ROAD TRIPS',
    evTitle: 'The map knows more than where you are.',
    evBody: 'Give Tragenda your vehicle, range, departure charge and charging preferences. It turns them into route-aware planning context you can actually use.',
    evPoints: ['Arrival-charge estimates', 'Live stall availability when providers return it', 'Along-route and around-me alternatives', 'Connector, speed and preferred-network context', 'Cold-weather and terrain assumptions', 'Fast refresh before you commit'],
    detour: 'Detour impact', available: '6 of 12 available', arrival: 'Arrive with 31%', routeTools: 'Route tools',
    togetherEyebrow: 'TRAVEL IS A GROUP SPORT',
    togetherTitle: 'One place for the conversations that move the trip.',
    togetherBody: 'A message can stay a message, become a decision, affect the map or become a shared expense. The context travels with it.',
    workflow: [['Talk → decision', 'Keep the discussion beside the trip and turn only the ideas that need agreement into a vote.'], ['Decision → route', 'Approved places return to the plan and map with their real distance, drive-time and detour impact.'], ['Trip → balance', 'Track receipts, currencies, expense groups and guests without exposing private trip conversations.']],
    journeyEyebrow: 'NOT JUST A PLANNER', journeyTitle: 'Useful before the trip. Calmer on the road. Worth opening after.',
    journey: [
      ['01', 'Plan', 'Collect places, shape days, calculate the route and invite your people.'],
      ['02', 'Get ready', 'Assign bookings, documents and packing without the follow-up noise.'],
      ['03', 'Go', 'See the next stop, navigate, react to changes and keep the group in sync.'],
      ['04', 'Remember', 'Save moments, close balances and turn selected stops into a public guide.'],
    ],
    trustEyebrow: 'PRIVATE BY DEFAULT', trustTitle: 'Your private trip is not content for the internet.',
    trustBody: 'Trip membership controls access. Public guides are separate copies containing only the stops you deliberately choose — never private chat, bookings, balances or traveller details.',
    trustPoints: ['App integrity protection', 'Account deletion in the app', 'No advertising trackers', 'Clear privacy controls'],
    afterEyebrow: 'AFTER THE TRIP', afterTitle: 'Close the balances. Keep the road book.', afterBody: 'The trip does not become a dead itinerary. Save private moments, understand where the money went, and publish only the stops you deliberately choose.', afterPoints: ['Private story by default', 'Clear multi-currency settlement', 'Selective public guide sharing'],
    pricingEyebrow: 'FREE TO PLAN · PAID WHEN POWER MATTERS', pricingTitle: 'No ads in your trip. No fee for every friend.', pricingBody: 'The complete manual trip stays free. One host can add premium intelligence for a single important trip or every trip they lead.',
    freePlan: 'Free', freePlanBody: 'Unlimited manual trips, people, planning, packing, chat, basic money and memories.', tripPass: 'Trip Pass', tripPassBody: 'A one-off group unlock for smarter routing, offline tools, Capture, exports and day artwork.', plusPlan: 'Plus', plusPlanBody: 'The same premium tools across every trip you host, with higher fair-use limits.', alwaysFree: 'Start free', paidSoon: 'Purchases coming with store release', pricingPromise: 'Your existing plan and data stay readable when a paid period ends.',
    storesEyebrow: 'TRAGENDA, WHEREVER THE ROAD STARTS', storesTitle: 'Plan on the web. Take Tragenda with you.',
    storesBody: 'The web app is open today. iPhone and Android releases are currently in private testing and are coming to the stores soon.',
    web: 'Open Tragenda on the web', comingSoon: 'Coming soon', appStore: 'Download on the App Store', playStore: 'Get it on Google Play',
    testingNote: 'iPhone and Android builds are currently available to invited internal testers.',
    finalEyebrow: 'THE ROAD AHEAD', finalTitle: 'Give the trip in your head somewhere to live.', finalBody: 'Choose a place. Shape your day, then save it when you’re ready.',
    company: 'A product by Archura Digital Solutions AB · Stockholm, Sweden',
  },
  tr: {
    nav: ['Ürün', 'EV ve harita', 'Birlikte', 'Gezi sonrası', 'Planlar'],
    login: 'Web uygulamasını aç',
    eyebrow: 'ÖNCESİ, YOLCULUK VE SONRASI · TEK YAŞAYAN PLAN',
    title: 'Tragenda senin', titleAccent: 'seyahat ajandan.',
    subtitle: 'Aklındaki road trip’i planla. Her durağın neyi değiştirdiğini gör. Herkesi hazırla. Eve dönünce hikâyeyi yanında tut.',
    primary: 'Planlamaya başla', secondary: 'Ürünü keşfet',
    proof: ['Varsayılan olarak özel', 'Tek başına ve grupla seyahat için', 'EV ve yakıt duyarlı'],
    productEyebrow: 'TÜM GEZİ, TEK YERDE', productTitle: 'Daha az gezi yönetimi. Önünde daha çok yol.',
    productBody: 'Yaşayan gezi planı rotayı, insanları ve yolculuğun ürettiği her şeyi birbirine bağlar — seyahati proje yönetimine çevirmeden.',
    features: [
      ['Yaşayan gezi planı', 'Günleri ve durakları gerçek rota üzerinde kur. Plan değiştiğinde yolculuğun geri kalanı ona ayak uydursun.'],
      ['Harita zekâsı', 'Bir yeri eklemeden sürüş süresini, mesafeyi, sapma etkisini ve yol üzerinde gerçekten ne olduğunu gör.'],
      ['Konuş ve karar ver', 'Sohbet planın yanında kalsın; fikri oylamaya çevir, sonucu herkesin bulacağı yerde bırak.'],
      ['Kendini açıklayan para', 'Para birimleri, fişler, gruplar, misafirler, bakiyeler ve kimin kime borçlu olduğunu ayrı tablo olmadan yönet.'],
      ['Eşyalar ve hazırlık', 'Her çanta veya araba için liste oluştur. Kendi kategorilerini ekle, kimin ne getireceğini gör.'],
      ['Hikâye ve paylaşım', 'Özel anları sakla, yolculuktan sonra yalnız seçtiğin durakları yeniden kullanılabilir bir rehber olarak yayınla.'],
    ],
    evEyebrow: 'EV İLE YOLCULUK İÇİN', evTitle: 'Harita, nerede olduğundan fazlasını bilir.',
    evBody: 'Aracını, menzilini, başlangıç şarjını ve şarj tercihlerini gir. Tragenda bunları gerçekten kullanılabilir rota bağlamına dönüştürür.',
    evPoints: ['Tahmini varış şarjı', 'Sağlayıcı sunduğunda canlı soket müsaitliği', 'Rota üzerinde ve çevrende alternatifler', 'Soket, hız ve tercih edilen ağ bilgisi', 'Soğuk hava ve arazi varsayımları', 'Karardan önce hızlı yenileme'],
    detour: 'Sapmanın etkisi', available: '12 soketten 6’sı boş', arrival: '%31 ile varış', routeTools: 'Rota araçları',
    togetherEyebrow: 'SEYAHAT BİR TAKIM İŞİ', togetherTitle: 'Geziyi ilerleten konuşmalar için tek yer.',
    togetherBody: 'Mesaj mesaj olarak kalabilir, karara dönüşebilir, haritayı etkileyebilir veya ortak masraf olabilir. Bağlam hep yanında kalır.',
    workflow: [['Sohbet → karar', 'Konuşma gezinin yanında kalsın; yalnızca uzlaşma gereken fikirleri oylamaya dönüştür.'], ['Karar → rota', 'Onaylanan yerler gerçek mesafe, sürüş süresi ve sapma etkisiyle plana ve haritaya geri döner.'], ['Gezi → bakiye', 'Özel gezi konuşmalarını açığa çıkarmadan fişleri, para birimlerini, masraf gruplarını ve misafirleri takip et.']],
    journeyEyebrow: 'SADECE BİR PLANLAYICI DEĞİL', journeyTitle: 'Gezi öncesi faydalı. Yolda sakin. Sonrasında yeniden açmaya değer.',
    journey: [
      ['01', 'Planla', 'Yerleri topla, günleri şekillendir, rotayı hesapla ve insanlarını davet et.'],
      ['02', 'Hazırlan', 'Rezervasyon, belge ve eşyaları takip mesajları olmadan paylaştır.'],
      ['03', 'Yola çık', 'Sıradaki durağı gör, navigasyonu aç, değişikliklere uyum sağla ve grubu güncel tut.'],
      ['04', 'Hatırla', 'Anları sakla, bakiyeleri kapat ve seçilen durakları herkese açık rehbere dönüştür.'],
    ],
    trustEyebrow: 'VARSAYILAN OLARAK ÖZEL', trustTitle: 'Özel gezin internet için içerik değildir.',
    trustBody: 'Erişimi gezi üyeliği belirler. Herkese açık rehberler yalnız bilinçli seçtiğin duraklardan oluşan ayrı kopyalardır — özel sohbet, rezervasyon, bakiye veya yolcu detayı içermez.',
    trustPoints: ['Uygulama bütünlüğü koruması', 'Uygulama içinden hesap silme', 'Reklam takipçisi yok', 'Açık gizlilik kontrolleri'],
    afterEyebrow: 'GEZİDEN SONRA', afterTitle: 'Bakiyeleri kapat. Yol kitabını sakla.', afterBody: 'Gezi ölü bir rota listesine dönüşmez. Özel anları sakla, paranın nereye gittiğini anla ve yalnız bilinçli seçtiğin durakları yayınla.', afterPoints: ['Varsayılan olarak özel hikâye', 'Açık çoklu para birimi hesabı', 'Seçerek herkese açık rehber paylaşımı'],
    pricingEyebrow: 'PLANLAMAK ÜCRETSİZ · GÜÇ GEREKTİĞİNDE ÜCRETLİ', pricingTitle: 'Gezinde reklam yok. Her yol arkadaşı için ayrı ücret yok.', pricingBody: 'Eksiksiz manuel gezi ücretsiz kalır. Bir ev sahibi, tek önemli geziye veya yönettiği tüm gezilere premium zekâ ekleyebilir.',
    freePlan: 'Ücretsiz', freePlanBody: 'Sınırsız manuel gezi, kişi, plan, hazırlık, sohbet, temel masraf ve anılar.', tripPass: 'Trip Pass', tripPassBody: 'Akıllı rota, çevrimdışı araçlar, Capture, dışa aktarma ve gün görselleri için tek seferlik grup kilidi.', plusPlan: 'Plus', plusPlanBody: 'Daha yüksek adil kullanım limitleriyle sahibi olduğun tüm gezilerde aynı premium araçlar.', alwaysFree: 'Ücretsiz başla', paidSoon: 'Satın almalar mağaza sürümüyle geliyor', pricingPromise: 'Ücretli dönem bitince mevcut planın ve verilerin okunabilir kalır.',
    storesEyebrow: 'YOL NEREDE BAŞLARSA TRAGENDA ORADA', storesTitle: 'Web’de planla. Tragenda’yı yanına al.',
    storesBody: 'Web uygulaması bugün açık. iPhone ve Android sürümleri özel testte; yakında mağazalarda.',
    web: 'Tragenda’yı web’de aç', comingSoon: 'Yakında', appStore: 'App Store’dan indir', playStore: 'Google Play’den edin',
    testingNote: 'iPhone ve Android sürümleri şu anda davetli iç test kullanıcılarına açık.',
    finalEyebrow: 'ÖNÜNDEKİ YOL', finalTitle: 'Aklındaki geziye yaşayacağı bir yer ver.', finalBody: 'Bir yer seç. Gününü şekillendir, hazır olduğunda kaydet.',
    company: 'Archura Digital Solutions AB ürünü · Stockholm, İsveç',
  },
  sv: {
    nav: ['Produkt', 'Elbil & karta', 'Tillsammans', 'Efter resan', 'Planer'],
    login: 'Öppna webbappen',
    eyebrow: 'EN LEVANDE PLAN · FÖRE, UNDER & EFTER', title: 'Tragenda är din', titleAccent: 'reseagenda.',
    subtitle: 'Planera roadtrippen i ditt huvud. Se vad varje stopp förändrar. Gör alla redo. Behåll berättelsen när ni kommer hem.',
    primary: 'Börja planera', secondary: 'Utforska produkten',
    proof: ['Privat som standard', 'För solo- och gruppresor', 'Anpassad för elbil och bränsle'],
    productEyebrow: 'HELA RESAN, PÅ ETT STÄLLE', productTitle: 'Mindre reseadmin. Mer väg framför er.',
    productBody: 'En levande resplan kopplar ihop rutten, människorna och allt resan skapar — utan att göra resan till projektledning.',
    features: [
      ['Levande resplan', 'Bygg dagar och stopp runt en riktig rutt. När planen flyttar sig följer resten av resan med.'],
      ['Kartintelligens', 'Se körtid, avstånd, omväg och vad som faktiskt ligger längs vägen innan du lägger till det.'],
      ['Prata & besluta', 'Ha chatten bredvid planen, gör en idé till en omröstning och lämna beslutet där alla hittar det.'],
      ['Pengar som förklarar sig', 'Hantera valutor, kvitton, grupper, gäster, saldon och vem som är skyldig vem utan ett separat ark.'],
      ['Packa & redo', 'Skapa listor för varje väska eller bilen. Lägg till egna kategorier och se vem som tar med vad.'],
      ['Berättelse & delning', 'Spara privata ögonblick och publicera bara de stopp du väljer som en återanvändbar guide efter resan.'],
    ],
    evEyebrow: 'FÖR ROADTRIPS MED ELBIL', evTitle: 'Kartan vet mer än var du är.',
    evBody: 'Ge Tragenda ditt fordon, räckvidd, startladdning och laddpreferenser. De blir ett ruttmedvetet sammanhang du faktiskt kan använda.',
    evPoints: ['Beräknad laddning vid ankomst', 'Live-tillgänglighet när operatören erbjuder den', 'Alternativ längs rutten och nära dig', 'Kontakt, hastighet och favoritnätverk', 'Antaganden för kyla och terräng', 'Snabb uppdatering före beslut'],
    detour: 'Omvägens effekt', available: '6 av 12 lediga', arrival: 'Ankomst med 31 %', routeTools: 'Ruttverktyg',
    togetherEyebrow: 'RESOR ÄR EN LAGSPORT', togetherTitle: 'Ett ställe för samtalen som för resan framåt.',
    togetherBody: 'Ett meddelande kan förbli ett meddelande, bli ett beslut, påverka kartan eller bli en gemensam kostnad. Sammanhanget följer med.',
    workflow: [['Prat → beslut', 'Behåll diskussionen bredvid resan och gör bara idéer som kräver samsyn till en omröstning.'], ['Beslut → rutt', 'Godkända platser återgår till planen och kartan med verkligt avstånd, körtid och omväg.'], ['Resa → saldo', 'Följ kvitton, valutor, utgiftsgrupper och gäster utan att exponera privata resesamtal.']],
    journeyEyebrow: 'INTE BARA EN PLANERARE', journeyTitle: 'Användbar före resan. Lugnare på vägen. Värd att öppna efteråt.',
    journey: [
      ['01', 'Planera', 'Samla platser, forma dagar, beräkna rutten och bjud in ditt sällskap.'],
      ['02', 'Bli redo', 'Fördela bokningar, dokument och packning utan påminnelsebruset.'],
      ['03', 'Res', 'Se nästa stopp, navigera, reagera på förändringar och håll gruppen synkad.'],
      ['04', 'Minns', 'Spara ögonblick, stäng saldon och gör valda stopp till en offentlig guide.'],
    ],
    trustEyebrow: 'PRIVAT SOM STANDARD', trustTitle: 'Din privata resa är inte innehåll för internet.',
    trustBody: 'Resans medlemskap styr åtkomst. Offentliga guider är separata kopior med bara de stopp du väljer — aldrig privat chatt, bokningar, saldon eller resenärsuppgifter.',
    trustPoints: ['Skydd för appintegritet', 'Kontoborttagning i appen', 'Inga annonsspårare', 'Tydliga integritetskontroller'],
    afterEyebrow: 'EFTER RESAN', afterTitle: 'Stäng saldona. Behåll vägboken.', afterBody: 'Resan blir inte en död resplan. Spara privata ögonblick, förstå vart pengarna tog vägen och publicera bara stoppen du medvetet väljer.', afterPoints: ['Privat berättelse som standard', 'Tydlig avräkning i flera valutor', 'Selektiv delning av offentlig guide'],
    pricingEyebrow: 'GRATIS ATT PLANERA · BETALA NÄR KRAFTEN BEHÖVS', pricingTitle: 'Inga annonser i resan. Ingen avgift för varje vän.', pricingBody: 'Den kompletta manuella resan förblir gratis. En värd kan lägga till premiumintelligens för en viktig resa eller alla resor de leder.',
    freePlan: 'Gratis', freePlanBody: 'Obegränsat med manuella resor, personer, planering, packning, chatt, grundekonomi och minnen.', tripPass: 'Trip Pass', tripPassBody: 'Ett gruppköp för smartare rutter, offlineverktyg, Capture, export och dagsbilder.', plusPlan: 'Plus', plusPlanBody: 'Samma premiumverktyg för varje resa du är värd för, med högre rimliga användningsgränser.', alwaysFree: 'Börja gratis', paidSoon: 'Köp kommer med butikslanseringen', pricingPromise: 'Din befintliga plan och data förblir läsbara när en betalperiod slutar.',
    storesEyebrow: 'TRAGENDA, VAR VÄGEN ÄN BÖRJAR', storesTitle: 'Planera på webben. Ta med Tragenda.',
    storesBody: 'Webbappen är öppen idag. iPhone- och Android-versionerna är i privat testning och kommer snart till butikerna.',
    web: 'Öppna Tragenda på webben', comingSoon: 'Kommer snart', appStore: 'Hämta i App Store', playStore: 'Hämta på Google Play',
    testingNote: 'iPhone- och Android-versioner är just nu tillgängliga för inbjudna interna testare.',
    finalEyebrow: 'VÄGEN FRAMÅT', finalTitle: 'Ge resan i ditt huvud någonstans att leva.', finalBody: 'Välj en plats. Forma dagen och spara när du är redo.',
    company: 'En produkt från Archura Digital Solutions AB · Stockholm, Sverige',
  },
} satisfies Record<MarketingLanguage, Record<string, unknown>>;

const featureIcons = [Route, Map, Vote, WalletCards, PackageCheck, Camera];

export default function Landing() {
  const { t, i18n } = useTranslation();
  const { actualTheme, setTheme } = useTheme();
  const [menu, setMenu] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const language = (['en', 'tr', 'sv'].includes(i18n.resolvedLanguage?.slice(0, 2) ?? '') ? i18n.resolvedLanguage?.slice(0, 2) : 'en') as MarketingLanguage;
  const copy = pageCopy[language];
  const details = landingDetails[language];
  const navTargets = ['#product', '#ev-map', '#together', '#journey', '#pricing'];

  useEffect(() => {
    if (!menu) return;
    headerRef.current?.querySelector<HTMLElement>('nav a')?.focus({ preventScroll: true });
    const closeOutside = (event: PointerEvent) => { if (event.target instanceof Node && !headerRef.current?.contains(event.target)) setMenu(false); };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); setMenu(false); menuButtonRef.current?.focus(); } };
    const viewport = window.matchMedia('(min-width: 761px)');
    const closeOnResize = () => { if (viewport.matches) setMenu(false); };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    viewport.addEventListener('change', closeOnResize);
    return () => { document.removeEventListener('pointerdown', closeOutside); document.removeEventListener('keydown', closeOnEscape); viewport.removeEventListener('change', closeOnResize); };
  }, [menu]);

  return (
    <div className="site-landing">
      <header className="site-header" ref={headerRef}>
        <Link to="/" className="wordmark-link" aria-label={details.home}><Brand /></Link>
        <nav id="site-navigation" className={menu ? 'open' : ''} aria-label={details.navigation}>
          {(copy.nav as string[]).map((item, index) => <a key={item} href={navTargets[index]} onClick={() => setMenu(false)}>{item}</a>)}
          <Link to="/explore" onClick={() => setMenu(false)}>{t('nav.explore')}</Link>
          <label className="site-language">
            <Globe2 aria-hidden />
            <span className="sr-only">{details.language}</span>
            <NativeSelect aria-label={details.language} value={language} onChange={(event) => void i18n.changeLanguage(event.target.value)}>
              <option value="en">EN</option><option value="tr">TR</option><option value="sv">SV</option>
            </NativeSelect>
          </label>
          <Button variant="ghost" size="bare"
            type="button"
            className="site-theme-toggle"
            aria-label={actualTheme === 'dark' ? details.useLight : details.useDark}
            title={actualTheme === 'dark' ? details.light : details.dark}
            onClick={() => setTheme(actualTheme === 'dark' ? 'light' : 'dark')}
          >
            {actualTheme === 'dark' ? <Sun aria-hidden /> : <Moon aria-hidden />}
          </Button>
          <Link to="/start" className="button button-small button-ink" onClick={() => setMenu(false)}>{copy.login as string}<ArrowRight aria-hidden /></Link>
        </nav>
        <div className="site-mobile-access"><Link to="/login" className="site-account-link" onClick={() => setMenu(false)}>{t('auth.signIn')}</Link><Button variant="outline" size="bare" type="button" ref={menuButtonRef} className="icon-button site-menu-button" aria-label={menu ? details.closeMenu : details.openMenu} aria-expanded={menu} aria-controls="site-navigation" onClick={() => setMenu(open => !open)}>{menu ? <X /> : <Menu />}</Button></div>
      </header>

      <main>
        <section className="launch-hero">
          <div className="launch-hero-copy">
            <p className="eyebrow">{copy.eyebrow as string}</p>
            <h1>{copy.title as string}<em>{copy.titleAccent as string}</em></h1>
            <p className="launch-lead">{copy.subtitle as string}</p>
            <div className="button-row">
              <Link to="/start" className="button button-primary">{copy.primary as string}<ArrowRight aria-hidden /></Link>
              <a href="#product" className="button button-quiet">{copy.secondary as string}</a>
            </div>
            <div className="launch-proof-list">
              {(copy.proof as string[]).map((item) => <span key={item}><Check aria-hidden />{item}</span>)}
            </div>
          </div>
          <div className="launch-hero-planner"><PlannerExperience variant="landing" /></div>
        </section>

        <div className="launch-capability-rail" aria-label={details.capabilitiesLabel}>
          {details.capabilities.map(item => <span key={item}>{item}</span>)}
        </div>

        <section className="launch-product" id="product">
          <div className="launch-section-intro">
            <p className="eyebrow">{copy.productEyebrow as string}</p>
            <h2>{copy.productTitle as string}</h2>
            <p>{copy.productBody as string}</p>
          </div>
          <div className="launch-plan-story">
            <div className="launch-plan-device">
              <MobileProductShot src="/product/stockholm-mobile-plan.jpg" sourceHeight={844} alt={details.planAlt} caption={details.planCaption} />
            </div>
            <div className="launch-plan-copy">
              <p className="launch-chapter-number">{details.planChapter}</p>
              <h3>{details.planTitle}</h3>
              <p>{details.planBody}</p>
              <div className="launch-route-facts">
                <span><small>{details.vehicle}</small><strong>Volvo EX30</strong></span>
                <span><small>{details.route}</small><strong>{new Intl.NumberFormat(language).format(605.7)} km</strong></span>
                <span><small>{details.energy}</small><strong>{new Intl.NumberFormat(language).format(102.97)} kWh</strong></span>
              </div>
            </div>
          </div>
          <div className="launch-feature-grid">
            {(copy.features as string[][]).map(([title, body], index) => {
              const Icon = featureIcons[index];
              return <article className="launch-feature" key={title}>
                <span><Icon aria-hidden /></span>
                <div><h3>{title}</h3><p>{body}</p></div>
              </article>;
            })}
          </div>
        </section>

        <section className="launch-ev" id="ev-map">
          <div className="launch-ev-visual">
            <MobileProductShot src="/product/stockholm-mobile-map.jpg" sourceHeight={720} alt={details.mapAlt} caption={details.mapCaption} />
          </div>
          <div className="launch-ev-copy">
            <p className="eyebrow">{copy.evEyebrow as string}</p>
            <h2>{copy.evTitle as string}</h2>
            <p>{copy.evBody as string}</p>
            <div className="launch-ev-dashboard" aria-label={details.evExample}>
              <article><small>{details.departure}</small><strong>100%</strong><span>{details.departureHelp}</span></article>
              <article><small>{details.routeCharger}</small><strong>21 / 24</strong><span>{details.chargerState}</span></article>
              <article><small>{details.chargeTarget}</small><strong>80%</strong><span>{details.targetHelp}</span></article>
            </div>
            <ul>{(copy.evPoints as string[]).map((point) => <li key={point}><Check aria-hidden />{point}</li>)}</ul>
            <small>{details.estimates}</small>
          </div>
        </section>

        <section className="launch-together" id="together">
          <div className="launch-together-copy">
            <p className="eyebrow">{copy.togetherEyebrow as string}</p>
            <h2>{copy.togetherTitle as string}</h2>
            <p>{copy.togetherBody as string}</p>
          </div>
          <div className="launch-workflow">
            {(copy.workflow as string[][]).map(([label, body], index) => <WorkflowRow key={label} icon={index === 0 ? Vote : index === 1 ? Map : CircleDollarSign} label={label} body={body} />)}
          </div>
        </section>

        <section className="launch-journey" id="journey">
          <div className="launch-section-intro">
            <p className="eyebrow">{copy.journeyEyebrow as string}</p>
            <h2>{copy.journeyTitle as string}</h2>
          </div>
          <div className="launch-journey-grid">
            {(copy.journey as string[][]).map(([number, title, body]) => <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3><p>{body}</p>
            </article>)}
          </div>
          <div className="launch-after-trip">
            <div className="launch-after-copy">
              <p className="eyebrow">{copy.afterEyebrow as string}</p>
              <h3>{copy.afterTitle as string}</h3>
              <p>{copy.afterBody as string}</p>
              <ul>{(copy.afterPoints as string[]).map((point) => <li key={point}><Check />{point}</li>)}</ul>
            </div>
            <div className="launch-after-ui" aria-label={details.afterExample}>
              <article className="launch-memory-card"><small>{t('story.example')}</small><strong>{t('story.landingTitle')}</strong><blockquote>{t('story.landingBody')}</blockquote><span>{t('story.private')}</span></article>
              <article className="launch-money-card"><small>{details.tripSpend}</small><strong>{new Intl.NumberFormat(language, { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(4860)}</strong><div><span>{details.stay} · 46%</span><span>{details.food} · 31%</span><span>{details.road} · 23%</span></div></article>
            </div>
          </div>
        </section>

        <section className="launch-trust">
          <div>
            <p className="eyebrow">{copy.trustEyebrow as string}</p>
            <h2>{copy.trustTitle as string}</h2>
            <p>{copy.trustBody as string}</p>
          </div>
          <div className="launch-trust-list">
            {(copy.trustPoints as string[]).map((point, index) => <span key={point}>{index === 0 ? <ShieldCheck /> : index === 1 ? <Users /> : index === 2 ? <BellRing /> : <Globe2 />}<strong>{point}</strong></span>)}
          </div>
        </section>

        <section className="launch-pricing" id="pricing">
          <div className="launch-section-intro">
            <p className="eyebrow">{copy.pricingEyebrow as string}</p>
            <h2>{copy.pricingTitle as string}</h2>
            <p>{copy.pricingBody as string}</p>
          </div>
          <div className="launch-pricing-grid">
            <article><span><ShieldCheck /></span><p className="eyebrow">{details.core}</p><h3>{copy.freePlan as string}</h3><p>{copy.freePlanBody as string}</p><Link to="/start">{copy.alwaysFree as string}<ArrowRight /></Link></article>
            <article><span><Route /></span><p className="eyebrow">{details.singleTrip}</p><h3>{copy.tripPass as string}</h3><p>{copy.tripPassBody as string}</p><small>{copy.paidSoon as string}</small></article>
            <article><span><Sparkles /></span><p className="eyebrow">{details.everyTrip}</p><h3>{copy.plusPlan as string}</h3><p>{copy.plusPlanBody as string}</p><small>{copy.paidSoon as string}</small></article>
          </div>
          <p className="launch-pricing-promise"><Check />{copy.pricingPromise as string}</p>
        </section>

        <section className="launch-stores" id="download">
          <div className="launch-stores-copy">
            <p className="eyebrow">{copy.storesEyebrow as string}</p>
            <h2>{copy.storesTitle as string}</h2>
            <p>{copy.storesBody as string}</p>
            <Link to="/start" className="button button-primary">{copy.web as string}<ArrowRight /></Link>
            <div className="launch-store-actions">
              <div className="launch-store-badge"><span className="launch-store-symbol"></span><span><small>{copy.comingSoon as string}</small><strong>{copy.appStore as string}</strong></span></div>
              <div className="launch-store-badge"><span className="launch-store-symbol"><Play /></span><span><small>{copy.comingSoon as string}</small><strong>{copy.playStore as string}</strong></span></div>
            </div>
            <p className="launch-testing-note">{copy.testingNote as string}</p>
          </div>
          <div className="launch-store-phones" aria-label={details.appPreviews}>
            <MobileProductShot className="launch-store-map" src="/product/stockholm-mobile-map.jpg" sourceHeight={720} alt={details.mapPhoneAlt} />
            <MobileProductShot className="launch-store-plan" src="/product/stockholm-mobile-plan.jpg" sourceHeight={844} alt={details.planPhoneAlt} />
          </div>
        </section>

        <section className="launch-final-cta">
          <div><p className="eyebrow">{copy.finalEyebrow as string}</p><h2>{copy.finalTitle as string}</h2><p>{copy.finalBody as string}</p></div>
          <Link to="/start" className="button button-primary">{copy.primary as string}<ArrowRight /></Link>
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer-brand"><Brand /><p>{copy.company as string}</p><p>{details.companyNumbers}</p></div>
        <div className="site-footer-links">
          <div><strong>Tragenda</strong><Link to="/start">{details.webApp}</Link><Link to="/explore">{details.explore}</Link><a href="#ev-map">{details.evMaps}</a></div>
          <div><strong>{details.legal}</strong><Link to="/privacy">{details.privacy}</Link><Link to="/terms">{details.terms}</Link><Link to="/fair-use">{details.fairUse}</Link><Link to="/cookies">{details.cookies}</Link></div>
          <div><strong>{details.dataHelp}</strong><Link to="/data-rights">{details.gdpr}</Link><Link to="/kvkk">{details.kvkk}</Link><Link to="/support">{details.support}</Link><a href="mailto:info@tragenda.app">info@tragenda.app</a></div>
        </div>
        <div className="site-footer-bottom"><span>© {new Date().getFullYear()} Archura Digital Solutions AB</span><a href="https://archura.digital" target="_blank" rel="noreferrer">archura.digital <ArrowRight /></a></div>
      </footer>
    </div>
  );
}

function MobileProductShot({ src, sourceHeight, alt, caption, className = '' }: { src: string; sourceHeight: 720 | 844; alt: string; caption?: string; className?: string }) {
  return <figure className={`launch-mobile-shot ${className}`.trim()}>
    <div className="launch-mobile-shell">
      <span className="launch-mobile-speaker" aria-hidden />
      <div className="launch-mobile-screen" style={{ aspectRatio: `390 / ${sourceHeight}` }}>
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </div>
    </div>
    {caption ? <figcaption>{caption}</figcaption> : null}
  </figure>;
}

function WorkflowRow({ icon: Icon, label, body }: { icon: typeof Vote; label: string; body: string }) {
  return <article><span><Icon aria-hidden /></span><div><h3>{label}</h3><p>{body}</p></div><ChevronRight aria-hidden /></article>;
}
